import React from 'react'
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'

import { AppEmptyLine } from '@components'
import { dsColorNew } from '@palette'
import { headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { inUnit, money, signedMoney, sideDiffInUnit } from '../@services/amounts'
import { PointsCell, RatioCell } from './PushRows'


const cell = { fontSize: 12.5, py: 0.9, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }

function Row({ sipp, own, market, deltaPt, unit, lor, bold = false, onDetail }) {
	const ownDiff = sideDiffInUnit(own, unit, lor)
	const marketDiff = sideDiffInUnit(market, unit, lor)
	const sx = bold ? { '& td': { ...cell, fontWeight: 700, backgroundColor: '#F6F7F9', borderTop: `2px solid ${dsColorNew.grayBorder}` } } : { '& td': cell }
	return <TableRow hover={!bold} sx={sx}>
		<TableCell sx={{ fontWeight: 700, color: dsColorNew.textTitle }}>{sipp}</TableCell>
		<TableCell align="right">{money(inUnit(own?.start, unit, lor))}</TableCell>
		<TableCell align="right">{money(inUnit(own?.end, unit, lor))}</TableCell>
		<TableCell align="right"><RatioCell ratio={own?.change}/>{ownDiff != null && <Box component="span" sx={{ display: 'block', fontSize: 11, color: dsColorNew.textMuted, fontWeight: 400 }}>{signedMoney(ownDiff)}</Box>}</TableCell>
		<TableCell align="right">{money(inUnit(market?.end, unit, lor))}</TableCell>
		<TableCell align="right"><RatioCell ratio={market?.change}/>{marketDiff != null && <Box component="span" sx={{ display: 'block', fontSize: 11, color: dsColorNew.textMuted, fontWeight: 400 }}>{signedMoney(marketDiff)}</Box>}</TableCell>
		<TableCell align="right"><PointsCell pt={deltaPt}/></TableCell>
		<TableCell align="center">{!bold && <Button variant="text" size="small" onClick={onDetail} sx={{ textTransform: 'none', fontSize: 12.5 }}>Detail</Button>}</TableCell>
	</TableRow>
}

/**
 * "Movement by ACRISS" (criterion 17): the server's `by_acriss` rows and "Average of the sample" = the headline, as
 * returned (the average of each rate's change everywhere, criterion 7). "Detail" opens the ACRISS detail. The bars above it
 * (`MovementCard`, phase 2) draw the same figures.
 */
export default function MovementTable({ data, view, onDetail }) {
	const rows = data?.by_acriss || []
	const lor = data?.sample?.lor || view.lor
	const unit = view.unit
	const h = data?.headline
	if (!rows.length) return <AppEmptyLine>No ACRISS with a decision in this window.</AppEmptyLine>
	return <Box sx={{ overflow: 'auto' }}>
		<Table size="small" sx={{ minWidth: 760 }}>
			<TableHead><TableRow sx={{ '& th': { ...headCellSx, fontSize: 11, whiteSpace: 'nowrap' } }}>
				<TableCell>ACRISS</TableCell><TableCell align="right">Before</TableCell><TableCell align="right">Now</TableCell><TableCell align="right">You</TableCell>
				<TableCell align="right">Tier now</TableCell><TableCell align="right">Tier</TableCell><TableCell align="right">Δ</TableCell><TableCell align="center"/>
			</TableRow></TableHead>
			<TableBody>
				{rows.map(r => <Row key={r.sipp} sipp={r.sipp} own={r.own} market={r.market} deltaPt={r.delta_pt} unit={unit} lor={lor} onDetail={() => onDetail(r.sipp)}/>)}
				{!!h && <Row sipp="Average of the sample" own={h.own} market={h.market} deltaPt={h.delta_pt} unit={unit} lor={lor} bold/>}
			</TableBody>
		</Table>
		<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1 }}>
			The average of each rate's change over the same pick-up dates and rental length (each rate: price now ÷ before − 1): every comparable rate weighs the same, so the Average row is the headline. Δ = your change − your tier's change, in points.
		</Typography>
	</Box>
}
